/**
 * Client for the cloud device relay.
 *
 * The path for a device that isn't the station itself — a tablet at the bench.
 * The loopback bridge can't reach one: a page served over HTTPS may talk to
 * `127.0.0.1` because loopback counts as potentially trustworthy, but that
 * exemption stops there, and `ws://192.168.x.x` is blocked everywhere. Here
 * both ends dial out to API Gateway, which already has a real certificate.
 *
 * Carries the same message envelope as the loopback bridge, so
 * {@link BridgeHandlers} serves both and the two paths can't drift.
 */
import { type BridgeDevice, type BridgeHandlers } from './bridge-transport';
import type { ScaleReading, ScaleStatus } from './hid-pos-scale';

/** Protocol version this client understands. */
const PROTOCOL_VERSION = 2;

/** Backoff between connection attempts. */
const RETRY_SCHEDULE_MS = [2_000, 5_000, 15_000, 30_000, 60_000];

/** Keepalive, inside API Gateway's ten-minute idle timeout. */
const PING_INTERVAL_MS = 5 * 60 * 1000;

/** The station a relayed frame came from, stamped on by the relay. */
export interface RelayStation {
  id: string;
  label?: string;
}

interface RelayScaleReading {
  pounds: number | null;
  stable: boolean;
  status: string;
  unitLabel: string;
}

type RelayMessage =
  | {
      v: number;
      type: 'devices';
      devices: BridgeDevice[];
      station?: RelayStation;
    }
  | { v: number; type: 'scale'; deviceId: string; reading: RelayScaleReading }
  | { v: number; type: 'station-gone'; station: RelayStation };

export interface RelayCredentials {
  /** Relay endpoint for this stage; empty means there isn't one. */
  url: string;
  /** The operator's Cognito ID token. */
  token: string;
  vendorId: string;
}

/**
 * A reconnecting relay connection.
 *
 * Unlike the loopback transport this one costs money to keep busy, so the
 * store closes it when the operator goes idle rather than holding it all shift
 * — see `bridge-suspend`. Reconnection is cheap and driven by the same activity
 * signals, so that's invisible in use.
 */
export class RelayTransport {
  private socket: WebSocket | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private attempts = 0;
  private stopped = true;
  private pingTimer: ReturnType<typeof setInterval> | null = null;

  /**
   * @param credentials - Where to dial and who as.
   * @param handlers - Shared with the loopback transport.
   */
  constructor(
    private credentials: RelayCredentials,
    private handlers: Omit<BridgeHandlers, 'onOpen' | 'onDevices'> & {
      onOpen: (stationName: string, devices: BridgeDevice[]) => void;
      /** One station's scales; several stations can share a vendor. */
      onDevices: (devices: BridgeDevice[], station?: RelayStation) => void;
      /** A station disconnected from the relay, taking its scales with it. */
      onStationGone: (stationId: string) => void;
    },
  ) {}

  /** Connect, retrying until {@link stop}. */
  start(): void {
    this.stopped = false;
    this.attempts = 0;
    this.open();
  }

  /**
   * Try again now rather than at the next backoff step: someone just opened a
   * scale or printer menu, so a station that has come up since should appear
   * without a page reload. A no-op while connected or stopped.
   */
  retryNow(): void {
    if (this.stopped || this.socket) return;
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
    this.attempts = 0;
    this.open();
  }

  /** Attempt one connection. */
  private open(): void {
    if (this.stopped || this.socket) return;

    const { url, token, vendorId } = this.credentials;
    if (!url || !token || !vendorId) return;

    const target = new URL(url);
    // Query string because a browser can't set headers on a WebSocket
    // handshake. It's a short-lived ID token and the socket upgrades
    // immediately, but it can reach access logs if those are enabled.
    target.searchParams.set('token', token);
    target.searchParams.set('vendor', vendorId);
    target.searchParams.set('role', 'browser');

    let socket: WebSocket;
    try {
      socket = new WebSocket(target.toString());
    } catch {
      this.scheduleRetry();
      return;
    }
    this.socket = socket;

    socket.onopen = () => {
      this.attempts = 0;
      // The relay has no greeting of its own — a station announces its devices
      // when it sees a subscriber arrive, which is what fills the list.
      this.handlers.onOpen('Farmshare Desktop', []);
      this.pingTimer = setInterval(
        () => this.send({ v: PROTOCOL_VERSION, type: 'ping' }),
        PING_INTERVAL_MS,
      );
    };

    socket.onmessage = (event) => this.handleMessage(event);

    socket.onclose = () => {
      this.socket = null;
      if (this.pingTimer) clearInterval(this.pingTimer);
      this.pingTimer = null;
      this.handlers.onClose();
      this.scheduleRetry();
    };

    socket.onerror = () => {};
  }

  /**
   * Decode one frame.
   * @param event - The message event.
   */
  private handleMessage(event: MessageEvent): void {
    let message: RelayMessage;
    try {
      message = JSON.parse(String(event.data));
    } catch {
      return;
    }
    // A station on an older build is ignored outright rather than half-read —
    // a misparsed weight is worse than none.
    if (message.v !== PROTOCOL_VERSION) return;

    switch (message.type) {
      case 'devices':
        this.handlers.onDevices(message.devices, message.station);
        break;
      case 'station-gone':
        this.handlers.onStationGone(message.station.id);
        break;
      case 'scale':
        this.handlers.onReading(message.deviceId, {
          pounds: message.reading.pounds,
          stable: message.reading.stable,
          status: message.reading.status as ScaleStatus,
          unitLabel: message.reading.unitLabel,
          // The station decoded this already; no raw unit code survives the trip.
          unitCode: message.reading.pounds === null ? -1 : 0x0c,
        } satisfies ScaleReading);
        break;
      default:
        break;
    }
  }

  /**
   * Send a message up to the station.
   * @param message - Message to send.
   * @param message.v
   * @param message.type
   * @returns Whether it went out.
   */
  send(message: { v: number; type: string }): boolean {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return false;
    this.socket.send(JSON.stringify(message));
    return true;
  }

  /** Queue another attempt. */
  private scheduleRetry(): void {
    if (this.stopped || this.retryTimer) return;
    const delay =
      RETRY_SCHEDULE_MS[Math.min(this.attempts, RETRY_SCHEDULE_MS.length - 1)];
    this.attempts += 1;
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      this.open();
    }, delay);
  }

  /** Close and stop retrying. */
  stop(): void {
    this.stopped = true;
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.pingTimer = null;
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
    const { socket } = this;
    this.socket = null;
    socket?.close();
  }
}
